/**
 * Episode Management Navigation
 *
 * Shared navigation component for episode management pages.
 * Includes a language context dropdown that filters all views.
 */

import { NavLink, useLocation } from 'react-router-dom';
import { useLanguage } from 'contexts/LanguageContext';

export function EpisodeNavigation() {
  const location = useLocation();
  const { selectedLanguage, setSelectedLanguage, languages } = useLanguage();

  const navItems = [
    { name: 'Episodes List', path: '/episodes' },
    { name: 'Idea Generator', path: '/episodes/ideas' },
    { name: 'Improve Episode', path: '/episodes/eval' },
    { name: 'Personas', path: '/episodes/personas' },
  ];

  // Helper function to determine if a nav item should be active
  const isNavItemActive = (itemPath: string) => {
    // Episodes List should be active for /episodes, /episodes/new, and /episodes/:id/edit
    if (itemPath === '/episodes') {
      return (
        location.pathname === '/episodes' ||
        location.pathname === '/episodes/new' ||
        location.pathname.match(/^\/episodes\/[^/]+\/edit$/) !== null ||
        location.pathname.match(/^\/episodes\/[^/]+\/localizations/) !== null
      );
    }
    // Improve Episode: active for /episodes/eval and sub-paths
    if (itemPath === '/episodes/eval') {
      return location.pathname.startsWith('/episodes/eval');
    }
    // For other paths, use exact match
    return location.pathname === itemPath;
  };

  return (
    <div className="mb-4">
      {/* Language Context Dropdown */}
      <div className="mb-4 flex items-center gap-3">
        <label className="text-sm font-medium text-gray-700">Language:</label>
        <select
          value={selectedLanguage}
          onChange={(e) => setSelectedLanguage(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white min-w-[200px]"
        >
          {languages.map((lang) => (
            <option key={lang.code} value={lang.code}>
              {lang.name}
            </option>
          ))}
        </select>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {navItems.map((item) => {
            const isActive = isNavItemActive(item.path);
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={`whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
              >
                {item.name}
              </NavLink>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
